import { getCmsModal, getCmsOffers, getDownloadConfiguration } from '@services/cms.service';
import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { logger } from '@services/loggers';
import { fetchProductsOptions } from '@services/options.service';

import {
    ApiModalType,
    ApiOfferType,
    ApiProductType,
    QUOTE_TYPES,
    SapiPlanType,
    SapicContextualisedProductV5Payload,
    SapicResponseTypeV5,
} from '@app-types/api';
import { FaiProduct } from '@app-types/fai';
import { PlanType, Product } from '@app-types/product';

import { API_X_SOURCE, API_X_SOURCE_WEB } from '@constants/api';

import { SapiMapper } from 'src/mappers/sapi-mapper';

import { SapicMapper } from '../mappers/sapic-mapper';
import { CACHE_TAGS } from './http/http';

const CATEGORY_OFFERS = 'forfaits_mcommerce';
const CATEGORY_CATALOG = 'catalogue_mcommerce';
const CATEGORY_HESITANT = 'forfaits_hesitant_mcommerce';
const config = getAppConfig();

export const BBOX_GENCODES = {
    PURE_FIBRE: config.offers.pureFibreGencode,
    BBOX_PLUS_TV: config.offers.bboxPlusTvGencode,
};

export async function getFaiProducts(): Promise<FaiProduct[] | undefined> {
    try {
        const configCE = await getDownloadConfiguration();
        const offersIds = configCE?.offres_fai.map((o) => {
            return o.offre.split('/')[1];
        });
        const path = config.bff.faiProducts || '';
        try {
            const allProducts = await apiHttpService.get<FaiProduct[]>(path);
            return allProducts
                ?.filter((fai) => offersIds?.includes(String(fai.id)))
                .sort((a, b) => (offersIds?.indexOf(String(a.id)) || 0) - (offersIds?.indexOf(String(b.id)) || 0));
        } catch (e) {
            logger.error('Error fetch fai products', e);
        }
    } catch (e) {
        logger.error('Error fetch fai config', e);
    }
}

export async function fetchProductsAndConf() {
    const initialPayload = getPayload();
    const payloadWithPureFibre = getPayload(BBOX_GENCODES.PURE_FIBRE);
    const payloadWithBboxPlusTv = getPayload(BBOX_GENCODES.BBOX_PLUS_TV);
    const appConfig = getAppConfig();
    const [plansResult, pureFibreResult, bboxPlusTvResult, otisRepeatorResult] = await Promise.allSettled([
        callSapicApi(initialPayload),
        callSapicApi(payloadWithPureFibre),
        callSapicApi(payloadWithBboxPlusTv),
        callSapicApi(payloadWithPureFibre, `gencodes in ("${appConfig.offers.wifiRepeaterGencode}")`, API_X_SOURCE_WEB),
    ]);

    const plans = plansResult.status === 'fulfilled' ? plansResult.value : [];
    const plansWithPureFibreInBody = pureFibreResult.status === 'fulfilled' ? pureFibreResult.value : [];
    const plansWithBboxPlusTvInBody = bboxPlusTvResult.status === 'fulfilled' ? bboxPlusTvResult.value : [];
    const otisRepeator = otisRepeatorResult.status === 'fulfilled' ? otisRepeatorResult.value : [];

    const plansWithIncitationBundlePromo = addBboxDiscountedPricesToPlans(
        plans,
        plansWithPureFibreInBody,
        plansWithBboxPlusTvInBody,
    );

    const productsWithDetails = await fetchProductsOptions(plansWithIncitationBundlePromo);

    const cmsData = {
        offers: await getCmsOffers(),
        modal: await getCmsModal(),
    };

    return mergeProductsData(productsWithDetails, cmsData, otisRepeator?.[0] && { repeater: otisRepeator[0] });
}

export async function fetchProduct(slug: string) {
    const product = await apiHttpService.get<SapiPlanType>(
        `${getAppConfig().bytelApi.url}/ventes/produits/${slug}?attribute=url_key`,
        undefined,
        { ttl: parseInt(getAppConfig().cache.ttl.products), tags: [CACHE_TAGS.PRODUCTS] },
    );

    if (!product) {
        throw new Error('Product not found');
    }

    return SapiMapper.mapToPlan(product);
}

export async function fetchProductByGencode(gencodes: string[]): Promise<Product[]> {
    if (!gencodes) {
        throw new Error('[Fetch Product] Missing gencodes parameters');
    }
    const listGencode = gencodes.join(',');
    const data = await apiHttpService.get<{ produits: { [key: string]: ApiProductType } }>(
        `${getAppConfig().bytelApi.url}/ventes/produits?listGencode=${listGencode}`,
        undefined,
        {
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        },
    );

    if (!data) {
        throw new Error('Products not found');
    }

    const products = Object.values(data.produits)?.map((product) => SapiMapper.mapToProduct(product));
    return products;
}

function mergeProductsData(
    planList: PlanType[],
    cmsData: {
        offers: ApiOfferType | null;
        modal: ApiModalType | null;
    },
    faiAdditionalProducts?: {
        repeater: Product;
    },
) {
    if (!planList) {
        logger.log('[ERROR][fetch planList failed]');
        throw new Error('failed');
    }

    if (Object.keys(planList).length === 0) {
        logger.error('[ERROR][analyzeProducts] failed, planList in entry is empty]');
        throw new Error('failed');
    }

    const completePlans = planList.map((plan) => {
        const productDirectUsInfos = getProductCmsData(plan.gencode, cmsData.offers);
        return completePlanData(plan, productDirectUsInfos);
    });

    return categorizeProducts(completePlans, faiAdditionalProducts);
}

const getProductCmsData = (gencode: string, mcoOffers: ApiOfferType | null): ApiOfferType[0] => {
    if (!mcoOffers) return {} as ApiOfferType[0];
    return mcoOffers.find((pr) => pr.gencode === gencode) || ({} as ApiOfferType[0]);
};

const completePlanData = (product: PlanType, productDirectUsInfos: ApiOfferType[0]): PlanType => {
    return {
        ...product,
        bannerText: productDirectUsInfos.bannerText,
        networkText: productDirectUsInfos.networkText || 'Qualité du réseau Bouygues Telecom',
        promoShowEndDate: productDirectUsInfos.promoShowEndDate,
        serviceMax: productDirectUsInfos.service_max,
        roaming: productDirectUsInfos.roaming,
        prime: productDirectUsInfos.prime,
        highLight: productDirectUsInfos.highLight,
    };
};

const categorizeProducts = (
    plans: PlanType[],
    faiAdditionalProducts?: {
        repeater: Product;
    },
) => {
    const offers = plans
        .filter((plan) => plan.categories.includes(CATEGORY_OFFERS))
        .sort((a, b) => a.position - b.position);
    const catalog = plans.filter((plan) => plan.categories.includes(CATEGORY_CATALOG));
    const hesitant = plans.filter((plan) => plan.categories.includes(CATEGORY_HESITANT));

    return {
        offers,
        catalog,
        hesitant,
        faiAdditionalProducts,
    };
};

const getPayload = (faiGencode?: string) => {
    const payload: SapicContextualisedProductV5Payload = {
        referenceExterneParcoursCible: 'idParcours-1',
        panier: {
            catalogue: 'WEB',
            acheteur: {
                type: 'NON_CONNU_GP',
            },
            parcours: [
                {
                    referenceExterne: 'idParcours-1',
                    typeParcours: QUOTE_TYPES.ACQUISITION,
                    elements: [],
                },
            ],
        },
    };
    if (faiGencode) {
        payload.panier.parcours.push({
            referenceExterne: 'idParcours-0',
            typeParcours: QUOTE_TYPES.ACQUISITION_FIXE,
            elements: [
                {
                    referenceExterne: 'idParcours-0',
                    type: 'ELEMENT_PANIER',
                    composantCommercial: {
                        identifiant: faiGencode,
                        typeCatalogue: 'BYTEL',
                    },
                },
            ],
        });
    }
    return payload;
};

const callSapicApi = async (
    payload: SapicContextualisedProductV5Payload,
    filtre = 'type in ("plan_sowo")',
    xSource = API_X_SOURCE,
) => {
    const headers: Record<string, string> = {
        'x-version': '5',
        'x-source': xSource,
    };
    const params = new URLSearchParams({
        clientCategorie: 'GP',
        modePourFinancement: 'auto',
        prixReferencePourFiltre: 'initial',
        tri: 'nouveautes',
        detail: '0',
        limite: '12',
        decalage: '0',
        prixReferencePourTri: 'initial',
        filtre,
    });

    const path = `${getAppConfig().bytelApi.url}/catalogue/produits-contextualises?${params}`;

    const plans = await apiHttpService
        .post<SapicResponseTypeV5>(path, payload, {
            headers,
            ttl: parseInt(getAppConfig().cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        })
        .then((result) => result.produits.map((product) => SapicMapper.mapToPlan(product)))
        .catch((error) => {
            console.error('[SAPIC] Error', error);
            return [];
        });

    return plans;
};

const addBboxDiscountedPricesToPlans = (
    plans: PlanType[],
    plansWithPureFibreInBody: PlanType[],
    plansWithBboxPlusTvInBody: PlanType[],
) => {
    return plans.map((plan) => {
        const planWithIncitativeDiscountPureFibre = plansWithPureFibreInBody.find((p) => p.gencode === plan.gencode);
        const planWithIncitativeDiscountBboxPlusTv = plansWithBboxPlusTvInBody.find((p) => p.gencode === plan.gencode);
        if (
            (planWithIncitativeDiscountPureFibre &&
                planWithIncitativeDiscountPureFibre.prices.final !== plan.prices.final) ||
            (planWithIncitativeDiscountBboxPlusTv &&
                planWithIncitativeDiscountBboxPlusTv.prices.final !== plan.prices.final)
        ) {
            return {
                ...plan,
                prices: {
                    ...plan.prices,
                    pricesWithBboxIncentive: {
                        pureFibre: planWithIncitativeDiscountPureFibre?.prices.final,
                        bboxPlusTv: planWithIncitativeDiscountBboxPlusTv?.prices.final,
                    },
                },
            };
        }
        return plan;
    });
};
